import React, { useEffect, useState } from "react";
import "../../styles/DMQLogin.css";
import { Col, Row } from "antd";
import OtpVerificationModal from "../../components/common/OTPVerificationModal";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { NTIDRule } from "../../utils/validations";
import dayjs from "dayjs";
import HLifeLogoTagLine from "../../assets/hlLogo-tagline.png";
import { appRouter } from "../../constants/routerConstant";
import { showCatchError } from "../../utils/toastMessageUtil";
import { maskingUtil } from "../../utils/maskingUtil";

import {
  clearCurrentInternalStep,
  clearCurrentStep,
  clearDMQMedicalConditions,
  clearDMQStepFlow,
  clearFormData,
  clearQuestionBank,
  clearQuestionSetId,
  getCurrentInternalStep,
  getCurrentQuestionSetId,
  getCurrentStep,
  getDMQMedicalConditions,
  getQuestionBank,
  getUser,
  getUserToken,
  saveCurrentInternalStep,
  saveCurrentStep,
  saveQuestionSetId,
  setEditingMode,
  setPDFPresent,
  setQuestionBank,
  setUser,
} from "../../utils/formStorage";

import { decrypt } from "../../utils/cryptoUtil";
import { loginGenerateOTP, loginVerifyOTP } from "../../services/serviceCollections/otpService";
import { checkSubmissionStatus, getQuestionSet } from "../../services/serviceCollections/dmqService";
import { clearSessionTimeStamp, setAuthData, setSessionTimeStamp } from "../../utils/authStorage";
import { MAIN_STEPS } from "../../constants/constants";
// import { TextField, DatePicker, Button, Card } from "@digitallabs/one-x-ui";
import { TextField, DatePicker, Button, Card } from "../../resourses/digitallabs/one-x-ui";
import { appendQuestionBank, CONDITION_TO_QSET } from "../../components/common/SplashScreenModal";

const DMQLogin = () => {
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const encrypted = searchParams.get("data");
  const [decrytedUserData, setDecryptedUserData] = useState(null);

  const [userObj, setUserObj] = useState(null);

  // Form state
  const [empId, setEmpId] = useState("");
  const [dob, setDob] = useState([]);

  // Validation
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const [showOTPModal, setShowOTPModal] = useState(false);
  const [formValues, setFormValues] = useState(null);

  const [touched, setTouched] = useState({
    empId: false,
    dob: false,
  });

  // ------------------- Decrypt Query Params -------------------
  useEffect(() => {
    if (!encrypted) return;

    (async () => {
      try {
        const decryptedString = await decrypt(encrypted);
        const params = new URLSearchParams(decryptedString);

        const parsedUser = Object.fromEntries(params.entries());
        // console.log("decryted string user object", parsedUser);
        setDecryptedUserData(parsedUser);
        setUserObj(parsedUser);

        const existingUser = await getUser();
        // console.log("existing user", existingUser);

        if (!existingUser) {
          setUser(encrypted);
        } else if (existingUser?.memberId !== parsedUser?.memberId) {
          clearFormData();
          setEditingMode(false);
          clearQuestionBank();
          clearQuestionSetId();
          clearCurrentStep();
          clearCurrentInternalStep();
          clearSessionTimeStamp();
          clearDMQStepFlow();
          clearDMQMedicalConditions();
          setUser(encrypted);
        } else {
          setUser(encrypted);
        }

        setEditingMode(false);
      } catch (error) {
        console.error("Error decrypting:", error);
      }
    })();
  }, [encrypted]);

  const isLinkExpired = (timestamp) => {
    const created = dayjs(timestamp);
    const now = dayjs();
    return now.diff(created, "day") > 30;
  };

  useEffect(() => {
    if (!userObj?.memberId || !userObj?.timestamp) return;

    const checkStatus = async () => {
      setLoading(true);

      if (isLinkExpired(userObj.timestamp)) {
        navigate(appRouter.LinkExpired);
        setLoading(false);
        return;
      }

      try {
        const result = await checkSubmissionStatus(userObj.memberId);

        if (result.message === "OK") {
          if (result?.data?.saved === true) {
            setPDFPresent(result.data.pdf || false);
            navigate(appRouter.SubmissionComplete);
          } else {
            setPDFPresent(false);
          }
        }
      } catch (error) {
        console.log("error on check submission", error);

        let status = error?.status;

        if (process.env.REACT_APP_ENABLE_ENCRYPTION === "true" && error?.data) {
          try {
            const decrypted = await decrypt(error.data);
            const parsed = JSON.parse(decrypted);

            console.log("error after encryption dec", parsed);
            status = parsed?.status ?? status;
          } catch (decryptError) {
            console.error("Failed to decrypt error response", decryptError);
          }
        }

        if (status === 503) {
          navigate(appRouter.MaintenanceMode, {
            state: {
              from: appRouter.DMQLogin,
              search: window.location.search,
              memberId: userObj.memberId,
            },
          });
          return;
        }

        showCatchError(error.message || error);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [userObj]);

  // ------------------- Validation -------------------
  const validateForm = () => {
    const newErrors = {};

    const ntidPattern = NTIDRule()[0].pattern;
    if (!empId) newErrors.emp_id = "Employee ID is required";
    else if (!ntidPattern.test(empId)) newErrors.emp_id = "Invalid Employee ID format";

    if (!dob) newErrors.dob = "Date of Birth is required";

    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  };

  useEffect(() => {
    validateForm();
  }, [empId, dob]);

  const handleSubmit = async () => {
    // mark all fields as touched
    setTouched({ empId: true, dob: true });

    validateForm();
    if (!isFormValid) return;

    if (empId !== decrytedUserData?.empCode) {
      showCatchError("Invalid user");
      return;
    }

    // proceed with OTP generation
    const currentUser = await getUser();
    const dobObj = dob?.[0];

    const formattedDob = dobObj ? `${dobObj.year}-${String(dobObj.month).padStart(2, "0")}-${String(dobObj.day).padStart(2, "0")}` : null;

    const payload = {
      emp_id: empId,
      dob: formattedDob,
      corp_id: parseInt(currentUser?.corpId),
      isForSubmission: false,
      member_info: getUserToken(),
    };

    setFormValues(payload);

    try {
      setLoading(true);
      const result = await loginGenerateOTP(payload);
      if (result.message === "OK") setShowOTPModal(true);
    } catch (error) {
      let extractedError = null;
      setLoading(false);

      if (process.env.REACT_APP_ENABLE_ENCRYPTION === "true") {
        const decrypted = await decrypt(error.data);
        const parsed = JSON.parse(decrypted);

        extractedError = parsed?.message || "Something went wrong. Please try again later.";
      } else {
        extractedError = error?.message || error;
      }

      showCatchError(extractedError);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (otp) => {
    const currentUser = await getUser();

    const payload = {
      emp_id: empId,
      corp_id: parseInt(currentUser?.corpId),
      otp: otp,
      isForSubmission: false,
    };

    try {
      setLoading(true);
      const result = await loginVerifyOTP(payload);
      setSessionTimeStamp();

      if (result.message === "OK") {
        setAuthData(result.data.result);
        populateQuestions();
      }
    } catch (error) {
      let extractedError = null;
      setLoading(false);
      if (process.env.REACT_APP_ENABLE_ENCRYPTION === "true") {
        const decrypted = await decrypt(error.data);
        const parsed = JSON.parse(decrypted);

        extractedError = parsed?.message || "Something went wrong. Please try again later.";
      } else {
        extractedError = error?.message || error;
      }

      showCatchError(extractedError);
    }
  };

  const handleResendOTP = async () => {
    try {
      setLoading(true);
      const result = await loginGenerateOTP(formValues);
      if (result.message === "OK") setShowOTPModal(true);
    } catch (error) {
      showCatchError(error.message || error);
    } finally {
      setLoading(false);
    }
  };

  const populateQuestions = async () => {
    const currentQuestionSetId = getCurrentQuestionSetId();
    try {
      setLoading(true);
      const result = await getQuestionSet();

      if (result.message === "OK") {
        const questionSetId = result?.data?._id;
        if (currentQuestionSetId !== questionSetId) {
          const set = result?.data?.content;
          clearFormData();
          clearQuestionBank();
          clearQuestionSetId();
          clearDMQMedicalConditions();
          clearDMQStepFlow();
          saveCurrentStep(MAIN_STEPS.PERSONAL_DETAILS);
          saveCurrentInternalStep(1);
          setQuestionBank(set);
          saveQuestionSetId(questionSetId);
        } else {
          const currentStep = getCurrentStep() ? getCurrentStep() : MAIN_STEPS.PERSONAL_DETAILS;
          const internalStep = getCurrentInternalStep() ? getCurrentInternalStep() : 1;
          saveCurrentStep(currentStep);
          saveCurrentInternalStep(internalStep);

          const existing = getQuestionBank();
          const requiredQSets = CONDITION_TO_QSET.filter(({ condition }) => getDMQMedicalConditions[condition]);
          const qSetsToFetch = requiredQSets.filter(({ qid }) => {
            return !Object.values(existing).some((category) => category?.some?.((q) => q?.question_id === qid));
          });
          const responses = await Promise.all(qSetsToFetch.map(({ qid }) => getQuestionSet({ q_set_id: qid })));
          let combinedContent = {};
          responses.forEach((res) => {
            const content = res?.data?.content;
            combinedContent = {
              ...combinedContent,
              ...content,
            };
          });
          appendQuestionBank(combinedContent);
        }
      }

      await navigate(appRouter.FormFilling);
    } catch (err) {
      showCatchError(err.message || err);
      setLoading(false);
    }
  };

  return (
    <Row gutter={0} style={{ margin: 0, height: "100vh" }} className="dmq-login-row">
      <Col span={24} className="login-content-col">
        <div className="login-wrapper">
          <Card className="login-card">
            <div className="login-logo">
              <img src={HLifeLogoTagLine} height={40} width={62.7} alt="HDFC Life" />
            </div>

            <div className="login-title">{showOTPModal ? "Verify your details using OTP" : "Secure your Group Term Insurance Cover!"}</div>

            <div className="login-sub-title">{showOTPModal ? `We’ve sent an OTP to your registered email (${maskingUtil("email", userObj?.email)})` : "Sign in and fill your Medical Questionnaire"}</div>

            {!showOTPModal ? (
              <div style={{ marginTop: -10 }}>
                <TextField
                  variant="underline"
                  label={{ label: "Employee ID", optional: false }}
                  placeholder="Enter"
                  maxLength={20}
                  value={empId}
                  onChange={(e) => {
                    setEmpId(e.target.value);
                    setTouched((prev) => ({ ...prev, empId: true }));
                  }}
                  helperText={{
                    message: touched.empId && errors.emp_id ? errors.emp_id : "",
                    status: touched.empId && errors.emp_id ? "error" : "success",
                  }}
                />
                <br />
                <DatePicker
                  placeholder="DD/MM/YYYY"
                  inputVariant="underline"
                  label={{ label: "Date of Birth", optional: false }}
                  value={dob}
                  selectionMode="single"
                  onValueChange={(details) => {
                    setDob(details.value);
                    setTouched((prev) => ({ ...prev, dob: true }));
                  }}
                  helperText={{
                    message: touched.dob && errors.dob ? errors.dob : "",
                    status: touched.dob && errors.dob ? "error" : "success",
                  }}
                />
                <br />

                <Button size="md" variant="primary" disabled={!isFormValid} onClick={handleSubmit} fullWidth>
                  Generate OTP
                </Button>
              </div>
            ) : (
              <OtpVerificationModal visible={showOTPModal} onCancel={() => setShowOTPModal(false)} onVerify={handleOTPVerify} otpLength={6} onResend={handleResendOTP} renderAsModal={false} />
            )}

            {!showOTPModal ? (
              <div className="know-more-card" onClick={() => navigate(appRouter.AppDisclaimer)}>
                <img src="/know_more_logo.png" alt="Know More" className="know-more-icon" height={48} />
                <span className="know-more-text">Know more about your medical questionnaire</span>
                <span className="know-more-arrow">›</span>
              </div>
            ) : null}
          </Card>
        </div>
      </Col>
    </Row>
  );
};

export default DMQLogin;
